import React, { useState } from 'react';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DangerZone — the "delete my account" surface. Guarded by two friction
 * checks: the user must type DELETE and re-enter their password. On success
 * we clear the session and bounce back to the landing page.
 */
const DangerZone = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const closeModal = () => {
    if (submitting) return;
    setConfirmOpen(false);
    setPassword('');
    setConfirmText('');
    setError('');
  };

  const canSubmit = confirmText === 'DELETE' && password.length >= 1 && !submitting;

  const handleDelete = async () => {
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    try {
      const token = localStorage.getItem('rbr_token');
      const res = await fetch(`${API_URL}/api/user/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || 'Could not delete account. Please try again.');
        setSubmitting(false);
        return;
      }
      toast.success('Your account has been deleted.');
      logout();
      navigate('/', { replace: true });
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="px-5 mb-8">
      <div
        className="rounded-2xl p-4 border"
        style={{ background: '#FFF5F0', borderColor: 'rgba(220,38,38,0.30)' }}
        data-testid="danger-zone"
      >
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(220,38,38,0.10)' }}
          >
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-red-700 mb-0.5">Danger zone</h3>
            <p className="text-[11px] text-red-500/80 leading-relaxed">
              Delete your account and every reflection, journal entry, streak, and badge
              tied to it. This can&rsquo;t be undone.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          data-testid="delete-account-open-btn"
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-[12px] font-semibold uppercase tracking-wide bg-red-500 text-white hover:bg-red-600 active:scale-[0.98] transition-all"
          style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete my account
        </button>
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in px-4"
          role="dialog"
          aria-modal="true"
          data-testid="delete-account-modal"
        >
          <div className="bg-[#FFFCF3] border border-red-500/30 rounded-3xl p-5 w-full max-w-[360px] shadow-2xl animate-scale-in relative">
            <button
              type="button"
              onClick={closeModal}
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-700"
              data-testid="delete-account-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h3
                className="text-base font-semibold text-red-700"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Delete account?
              </h3>
            </div>

            <p className="text-xs text-stone-600 leading-relaxed mb-4">
              This will permanently erase your profile, streaks, badges, journal entries,
              saved reflections, and every other trace of your account. It cannot be reversed.
            </p>

            <label className="text-[11px] font-semibold text-stone-600 mb-1 block">
              Type <span className="font-bold text-red-600">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck="false"
              className="w-full mb-3 px-3.5 py-2.5 bg-white border border-stone-300 rounded-xl text-sm text-stone-800 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20"
              data-testid="delete-account-confirm-input"
            />

            <label className="text-[11px] font-semibold text-stone-600 mb-1 block">
              Your password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Confirm with your password"
              autoComplete="current-password"
              className="w-full mb-2 px-3.5 py-2.5 bg-white border border-stone-300 rounded-xl text-sm text-stone-800 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20"
              data-testid="delete-account-password-input"
            />

            {error && (
              <div className="mb-3 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-[11px] text-red-500">{error}</p>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={closeModal}
                disabled={submitting}
                className="flex-1 py-2.5 bg-stone-100 text-stone-700 rounded-xl text-sm font-medium hover:bg-stone-200 disabled:opacity-60"
                data-testid="delete-account-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={!canSubmit}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 disabled:opacity-60 disabled:hover:bg-red-500"
                data-testid="delete-account-confirm-btn"
              >
                {submitting ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DangerZone;
